import { times, get } from 'lodash';
import moment from 'moment';
import type { ValidationState } from 'Store/trip-information/kids/constants';
import { DEFAULT_KID_VALUE, VALIDATION_STATE_ENUM } from 'Store/trip-information/kids/constants';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';
import 'Store/trip-information/kids/kids.reducer';
import {
  QUERY_CHILDCARE_FLAG,
  QUERY_DATE_FORMAT,
  QUERY_FACET_OPTIONS_DELIMITER,
  QUERY_KIDS_DELIMITER,
} from 'Utils/uri/constants';
import {
  ACTIVITIES_FACET_ID,
  CHILDCARE_CRITERIA_FACET_ID,
  CHILDCARE_FILTER_FACET_ID,
  CHILDCARE_FILTER_FACET_OPTION_0_TO_2,
  CHILDCARE_FILTER_FACET_OPTION_11_TO_17,
  CHILDCARE_FILTER_FACET_OPTION_2_TO_3,
  CHILDCARE_FILTER_FACET_OPTION_4_TO_10,
  COUNTRIES_FACET_ID,
  DATE_FORMAT,
  DEPARTURE_CITY_FACET_ID,
  DEPARTURE_DATE_DEFAULT_DELAY_IN_MONTH,
  DESTINATIONS_FACET_ID,
  FLIGHT_DURATION_FACET_ID,
  KIDS_NUMBER_MAX,
  KIDS_NUMBER_MAX_SELLER,
  SEASON_FACET_ID,
  SEASON_FACET_QUERY_PARAM_EN_TO_FR,
  SEASON_FACET_QUERY_PARAM_FR_TO_EN,
  TYPES_OF_STAY_FACET_ID,
} from './constants';

function findFacetCategoryById(
  facets: Array<SearchFacetCategory>,
  facetCategoryId: string,
): SearchFacetCategory | null | undefined {
  return facets.find((facetCategory) => get(facetCategory, 'id') === facetCategoryId);
}

function _getFacetValues(
  facets: Array<SearchFacetCategory>,
  categoryId: string,
): Array<FacetValue> {
  const category: SearchFacetCategory | null | undefined = findFacetCategoryById(
    facets,
    categoryId,
  );
  return get(category, 'list', []);
}

function getChildcareFacets(facets: Array<SearchFacetCategory>): Array<FacetValue> {
  return _getFacetValues(facets, CHILDCARE_CRITERIA_FACET_ID);
}

function getDepartureCities(facets: Array<SearchFacetCategory>): Array<FacetValue> {
  return _getFacetValues(facets, DEPARTURE_CITY_FACET_ID);
}

function getInactiveFacetValues(
  facets: Array<SearchFacetCategory>,
  activeFacets: any,
  facetId: string,
): Array<string> {
  const [matchingFacet] = facets.filter(({ id }) => id === facetId);
  const matchingFacetValues = matchingFacet?.list?.map(({ value }) => value) ?? [];
  const matchingActiveFacetValues = Object.keys(activeFacets[facetId] ?? {});
  return matchingFacetValues.filter((option) => !matchingActiveFacetValues.includes(option));
}

function filterFacetsToHide(
  facets: Array<SearchFacetCategory>,
  facetsToHide: any,
): Array<SearchFacetCategory> {
  const facetIdsWithOptionsToHide = Object.keys(facetsToHide ?? {});
  return facets.map((facet) => {
    if (facetIdsWithOptionsToHide.includes(facet.id)) {
      return {
        ...facet,
        list: facet.list.filter(({ value }) => !facetsToHide[facet.id].includes(value)),
      };
    }

    return facet;
  });
}

function getSelectedDepartureCity(facets: Array<SearchFacetCategory>): string | null | undefined {
  const selectedCity: FacetValue | null | undefined = getDepartureCities(facets).find((facet) =>
    get(facet, 'selected'),
  );
  return selectedCity ? get(selectedCity, 'name') : null;
}

type Date = number | string | moment$Moment;

function formatDepartureDateForStore(
  date: Date | null | undefined,
  format: string = DATE_FORMAT,
): timestamp | null | undefined {
  return date !== null ? moment(date, format).valueOf() : null;
}

function getDepartureDateDisplayValue(date: timestamp | null | undefined = null): string {
  const momentDate = moment(date);
  return momentDate.isValid() ? momentDate.format(DATE_FORMAT) : '';
}

function isDateInThePast(timestampDate?: timestamp): boolean {
  return timestampDate ? moment(timestampDate).isBefore(moment(), 'day') : false;
}

function getFormattedDepartureDate(date?: timestamp, dateFormat: string): string {
  return date ? moment(date).format(dateFormat) : '';
}

function isKidMinor(birthdate: timestamp): boolean {
  if (!birthdate) {
    return false;
  }

  const minBirthdate: moment = moment().subtract(18, 'years');
  const momentBirthdate: moment = moment(birthdate);
  return momentBirthdate.isValid() && momentBirthdate.isBetween(minBirthdate, moment());
}

function getChildren(numberOfChildren: number, childrenBirthdates: Array<string>): any {
  if (
    Number.isInteger(numberOfChildren) &&
    numberOfChildren >= 0 &&
    numberOfChildren <= KIDS_NUMBER_MAX
  ) {
    return times(numberOfChildren, (idx) =>
      childrenBirthdates && childrenBirthdates.length > idx
        ? {
            birthdate: moment(childrenBirthdates[idx], 'YYYYMMDD', true).valueOf(),
            childcareIncluded: false,
          }
        : {
            childcareIncluded: false,
          },
    );
  }

  return null;
}

function convertQueryParamToKids(value: string): Array<Kids> {
  if (!value) {
    return [];
  }

  return value.split(QUERY_KIDS_DELIMITER).map<Kids>((serializedKidString: string) => {
    const hasChildcareFlag = serializedKidString.substr(-1) === QUERY_CHILDCARE_FLAG;
    const strBirthdate = serializedKidString.replace(QUERY_CHILDCARE_FLAG, '');
    const birthdate = moment(strBirthdate, QUERY_DATE_FORMAT, true);
    const childcareIncluded = birthdate.isValid()
      ? hasChildcareFlag
      : DEFAULT_KID_VALUE.childcareIncluded;
    return {
      ...DEFAULT_KID_VALUE,
      childcareIncluded,
      birthdate: birthdate.isValid() ? birthdate.valueOf() : undefined,
    };
  });
}

function convertKidsToChildcareQueryParam({
  departureDate,
  kids,
}: {
  departureDate: timestamp | null | undefined;
  kids: Array<Kids>;
}): Array<string> {
  if (!departureDate) {
    return [];
  }

  return kids
    .reduce(
      (acc, { birthdate }) =>
        birthdate ? [...acc, moment(departureDate).diff(moment(birthdate), 'months')] : acc,
      [],
    )
    .map((diffInMonths) => {
      if (diffInMonths >= 4 && diffInMonths < 24) {
        return CHILDCARE_FILTER_FACET_OPTION_0_TO_2;
      }

      if (diffInMonths >= 24 && diffInMonths < 48) {
        return CHILDCARE_FILTER_FACET_OPTION_2_TO_3;
      }

      if (diffInMonths >= 48 && diffInMonths < 132) {
        return CHILDCARE_FILTER_FACET_OPTION_4_TO_10;
      }

      if (diffInMonths >= 132 && diffInMonths < 216) {
        return CHILDCARE_FILTER_FACET_OPTION_11_TO_17;
      }

      return '';
    })
    .filter(Boolean);
}

function unserializeKidsFromQueryParam(value: string, isSeller: boolean): Array<Kids> {
  const maxKidsNumber = isSeller ? KIDS_NUMBER_MAX_SELLER : KIDS_NUMBER_MAX;

  if (!value) {
    return [];
  }

  const kidsNumber = Number(value); // NOTE: DO NOT USE parseInt() !!! parseInt('2010-12-10c') will return 2010 ...

  if (kidsNumber > maxKidsNumber) {
    return [];
  }

  if (Number.isInteger(kidsNumber)) {
    return new Array(kidsNumber).fill({ ...DEFAULT_KID_VALUE });
  }

  const kids = convertQueryParamToKids(value);
  return kids.length > maxKidsNumber ? [] : kids;
}

/**
 * Helper to serialize kids array as a URI query parameter.
 *
 * Example of returns:
 *
 * - `kids=1` (1 kid, no birthdate)
 * - `kids=2010-10-30` (1 kid, with birthdate)
 * - `kids=_` (2 kids)
 * - `kids=2010-10-30=_` (2 kids, 1 birthdate)
 * - `kids=2010-10-30c=___`
 * - `kids=2010-10-30c_2009-04-01!`
 */
export const serializeKidsToQueryParam = (params: {
  kids: Array<Kids>;
  kidsValidationState: Array<ValidationState>;
}) => {
  const { kids, kidsValidationState } = params;
  const serializedKids = kids
    .map(({ birthdate, childcareIncluded }, index) => {
      if (
        !birthdate ||
        !kidsValidationState ||
        (kidsValidationState?.length > 0 &&
          kidsValidationState[index] !== VALIDATION_STATE_ENUM.valid)
      ) {
        return '';
      }

      const formattedBirthdate = moment(birthdate).format(QUERY_DATE_FORMAT);
      return childcareIncluded
        ? `${formattedBirthdate}${QUERY_CHILDCARE_FLAG}`
        : formattedBirthdate;
    })
    .filter((birthdate) => birthdate !== '');
  // Special case when there is only one kid, as we can't make a join('_') with only one item
  let serializedOutput = '';

  if (serializedKids.length === 1) {
    if (serializedKids.filter(Boolean).length === 0) {
      serializedOutput = '1';
    }

    // Return kid's birthdate
    if (serializedOutput !== '1') {
      serializedOutput = serializedKids.shift();
    }

    const childcareIncluded = kids.some((kid) => kid.childcareIncluded);
    return childcareIncluded ? `${serializedOutput}${QUERY_CHILDCARE_FLAG}` : serializedOutput;
  }

  return serializedKids.join(QUERY_KIDS_DELIMITER);
};
export const synchronizeChildcareQPWithKidsQP = ({
  childcareFacetQueryParam,
  departureDate,
  departureDateQueryParam,
  kids,
  kidsQueryParam = '',
}: {
  childcareFacetQueryParam: string;
  departureDate: number;
  departureDateQueryParam: string;
  kids: Array<Kids>;
  kidsQueryParam?: string;
}) => {
  const previousDepartureDate = formatDepartureDateForStore(departureDateQueryParam, DATE_FORMAT);
  const previousKids = convertQueryParamToKids(kidsQueryParam);
  const previousChildcareIncluded = previousKids.some((kid) => kid.childcareIncluded);
  const childcareIncluded = kids.some((kid) => kid.childcareIncluded);

  if (!childcareIncluded && !previousChildcareIncluded) {
    // childcareIncluded checkbox unchecked and unchanged => no override
    return undefined;
  }

  const childcareRemoved = previousChildcareIncluded && !childcareIncluded;
  const kidsRemoved = previousKids.length > 0 && kids.length === 0;

  if (childcareRemoved || kidsRemoved) {
    // childcareIncluded checkbox unchecked or kids removed from FilterWho => reset childcare filter
    return '';
  }

  // childcareIncluded checkbox checked => override childcare filter with matching kids age range and existing childcare options
  const childcareFilterOptionsFromPreviousKids = [
    ...new Set(
      convertKidsToChildcareQueryParam({
        departureDate: previousDepartureDate,
        kids: previousKids,
      }),
    ),
  ];
  const childcareFilterOptionsFromKids = [
    ...new Set(
      convertKidsToChildcareQueryParam({
        departureDate,
        kids,
      }),
    ),
  ];
  const childcareFilterOptionsToRemove = childcareFilterOptionsFromPreviousKids.filter(
    (option) => !childcareFilterOptionsFromKids.includes(option),
  );
  const chilcareFilterOptions = childcareFacetQueryParam.split(QUERY_FACET_OPTIONS_DELIMITER);
  return [
    ...new Set([
      ...childcareFilterOptionsFromKids,
      ...chilcareFilterOptions.filter((option) => !childcareFilterOptionsToRemove.includes(option)),
    ]),
  ]
    .filter(Boolean)
    .join(QUERY_FACET_OPTIONS_DELIMITER);
};
export const getKidAgeInMonths = ({
  birthdate,
  departureDate,
}: {
  birthdate?: timestamp | null | undefined;
  departureDate?: timestamp | null | undefined;
}) => {
  const defaultDepartureDate = moment()
    .add(DEPARTURE_DATE_DEFAULT_DELAY_IN_MONTH, 'months')
    .valueOf();
  const b = birthdate ? moment(birthdate) : moment().subtract(8, 'y');
  return moment(departureDate || defaultDepartureDate).diff(b, 'months');
};
export const customFacetsQueryParamMapping = {
  [FLIGHT_DURATION_FACET_ID]: 'f',
};
export const facetsQueryParamMapping = {
  [ACTIVITIES_FACET_ID]: 'a',
  [CHILDCARE_FILTER_FACET_ID]: 'ch',
  [COUNTRIES_FACET_ID]: 'c',
  [DESTINATIONS_FACET_ID]: 'd',
  [SEASON_FACET_ID]: 's',
  [TYPES_OF_STAY_FACET_ID]: 't',
};
export const getActiveFacetsFromQueryParams = (queryParams: {}, queryParamMapping: {}) =>
  Object.keys(queryParamMapping).reduce((facets, facetId) => {
    const activeFacets =
      queryParams[queryParamMapping[facetId]]?.split(QUERY_FACET_OPTIONS_DELIMITER) || [];
    facets = {
      ...facets,
      [facetId]: activeFacets.reduce((acc, activeFacet) => {
        let facet = decodeURI(activeFacet);

        if (facetId === SEASON_FACET_ID) {
          facet = SEASON_FACET_QUERY_PARAM_EN_TO_FR[facet] ?? facet;
        }

        acc[facet] = true;
        return acc;
      }, {}),
    };
    return facets;
  }, {});
export const generateActiveFacetsQueries = (activeFacets: {}, queryParamMapping: {}) =>
  Object.keys(queryParamMapping).reduce((facets, facetId) => {
    let facet = Object.keys(activeFacets[facetId] || {});

    if (facet.length && facetId === SEASON_FACET_ID) {
      facet = facet.map((label) => SEASON_FACET_QUERY_PARAM_FR_TO_EN[label] ?? label);
    }

    facets = {
      ...facets,
      [queryParamMapping[facetId]]: facet.length
        ? facet.join(QUERY_FACET_OPTIONS_DELIMITER)
        : undefined,
    };
    return facets;
  }, {});
export const hasDepartureCityAvailable = (
  departureCities: DepartureCities,
  departureCity: string,
) => departureCities?.some(({ value }) => value === departureCity);
export {
  filterFacetsToHide,
  findFacetCategoryById,
  formatDepartureDateForStore,
  getChildcareFacets,
  getChildren,
  getDepartureCities,
  getDepartureDateDisplayValue,
  getFormattedDepartureDate,
  getInactiveFacetValues,
  getSelectedDepartureCity,
  isDateInThePast,
  isKidMinor,
  unserializeKidsFromQueryParam,
};
